/**
 * Pattern Overlay Types — DRR-309 Phase 9
 *
 * Overlay patterns that augment the 8 core UiPatterns.
 * Core patterns are mutually exclusive (workflow state-driven).
 * Overlays are orthogonal (can appear on any base pattern).
 *
 * Three-tier stacking model:
 * - Layer 3 (Blocking): Takes full control, base pattern hidden
 * - Layer 2 (Persistent): Banners/badges that persist across all views
 * - Layer 1 (Contextual): Panels that appear based on context
 * - Layer 0 (Base): Core UiPattern from workflow state
 */

/**
 * Overlay pattern types that can be layered on top of core UiPatterns.
 * These are NOT workflow state-driven — they're event or context triggered.
 */
export type PatternOverlay =
  // Life-Safety Critical (Layer 3 - Blocking)
  | 'EmergencyOverride'
  | 'WitnessVerification'

  // Operational (Layer 2 - Persistent)
  | 'OfflineStatus'
  | 'EscalationTimer'
  | 'RegulatoryDeadline'
  | 'ShiftHandoff'
  | 'IncidentRapidEntry'
  | 'SurveyorAudit'

  // Enhancement (Layer 1 - Contextual)
  | 'CollaborationIndicator'
  | 'AIAssistant';

/** Priority levels for overlay stacking */
export type OverlayPriority = 'blocking' | 'persistent' | 'contextual';

/** Maps overlay types to their priority layer */
export const OVERLAY_PRIORITIES: Record<PatternOverlay, OverlayPriority> = {
  // Blocking (Layer 3) — take full control
  EmergencyOverride: 'blocking',
  WitnessVerification: 'blocking',

  // Persistent (Layer 2) — always visible banners
  OfflineStatus: 'persistent',
  EscalationTimer: 'persistent',
  RegulatoryDeadline: 'persistent',
  ShiftHandoff: 'persistent',
  IncidentRapidEntry: 'persistent',
  SurveyorAudit: 'persistent',

  // Contextual (Layer 1) — optional panels
  CollaborationIndicator: 'contextual',
  AIAssistant: 'contextual',
};

/** Configuration for a single overlay instance */
export interface OverlayConfig {
  type: PatternOverlay;
  enabled: boolean;
  props?: Record<string, unknown>;
}

/** Combined pattern state for JourneyView */
export interface JourneyPatternState {
  /** Current workflow state's UI pattern */
  basePattern: string; // UiPattern from WorkflowStateView
  /** Currently active overlays (in priority order) */
  activeOverlays: PatternOverlay[];
  /** Emergency/break-glass mode is active */
  emergencyActive: boolean;
  /** Working with stale data */
  offlineStale: boolean;
  /** Current escalation level (0 = none) */
  escalationLevel: number;
  /** Witness verification pending */
  witnessPending: boolean;
}

/** Staleness warning for offline status overlay */
export interface StalenessWarning {
  category: string;
  label: string;
  maxAgeSeconds: number;
  currentAgeSeconds: number;
  action: 'block' | 'confirm' | 'warn';
  lastSynced: Date | null;
}

/** Escalation state for timer overlay */
export interface EscalationTimerState {
  taskId: string;
  slaId: string;
  secondsRemaining: number;
  currentLevel: number;
  totalLevels: number;
  escalationChain: string[];
  currentOwner: string;
  nextLevelAt: number;
}

/** Witness record for verification overlay */
export interface WitnessRecord {
  witnessId: string;
  witnessName: string;
  witnessRole: string;
  signedAt: Date;
  expiresAt: Date;
  isExpired: boolean;
}

/** Break-glass justification for emergency override */
export interface BreakGlassJustification {
  reason: 'life_threatening' | 'system_unavailable' | 'other';
  explanation?: string;
  regulatoryOverride?: string;
  witnessId?: string;
}

/** Shift handoff state */
export interface ShiftHandoffState {
  outgoingStaff: {
    id: string;
    name: string;
    role: string;
  };
  incomingStaff: {
    id: string;
    name: string;
    role: string;
  } | null;
  shiftType: string;
  completedTasks: number;
  inProgressTasks: number;
  pendingTasks: number;
  handoffNotes: string;
}

/** Collaboration presence for indicator overlay */
export interface CollaborationPresence {
  userId: string;
  userName: string;
  avatarUrl?: string;
  activeField?: string;
  lastSeen: Date;
}

/** AI suggestion for assistant overlay */
export interface AISuggestion {
  id: string;
  type: 'next_action' | 'field_value' | 'routing' | 'warning';
  title: string;
  description: string;
  confidence: number; // 0-100
  action?: {
    type: string;
    params: Record<string, unknown>;
  };
}

/** Regulatory deadline for deadline overlay */
export interface RegulatoryDeadline {
  id: string;
  label: string;
  citation: string;
  deadlineAt: Date;
  startedAt: Date;
  percentRemaining: number;
  status: 'on_track' | 'at_risk' | 'breached' | 'completed';
}
